import {
  arrowPath,
  archPath,
  bookmarkPath,
  chevronArrowPath,
  gearPath,
  halfCirclePath,
  kitePath,
  pacmanPath,
  parallelogramPath,
  plusPath,
  quarterCirclePath,
  regularPolygonPath,
  ringPath,
  scallopPath,
  starPath,
  trapezoidPath,
  xMarkPath,
} from './shapeGenerators';
import {
  CHECKMARK_PATH,
  CLOUD_PATH,
  CLUB_PATH,
  CRESCENT_PATH,
  CROWN_PATH,
  EGG_PATH,
  HEART_PATH,
  HOURGLASS_PATH,
  LEAF_PATH,
  LIGHTNING_PATH,
  PIN_HOLLOW_PATH,
  PIN_PATH,
  SEAL_LOBES,
  SHIELD_PATH,
  SPADE_PATH,
  SPEECH_BUBBLE_OVAL_PATH,
  SPEECH_BUBBLE_RECT_PATH,
  TEARDROP_PATH,
} from './shapeIcons';

export type ShapeDefinition =
  | {
      title: string;
      kind: 'rect';
      width: number;
      height: number;
      rx?: number;
      ry?: number;
    }
  | { title: string; kind: 'circle'; radius: number }
  | { title: string; kind: 'ellipse'; rx: number; ry: number }
  | {
      title: string;
      kind: 'path';
      d: string;
      fillRule?: 'nonzero' | 'evenodd';
    };

export const shapeDefinitions: ShapeDefinition[] = [
  // Row 1 - the 3 most commonly used basic shapes come first, always
  // visible without scrolling.
  { title: 'Square', kind: 'rect', width: 94, height: 94 },
  { title: 'Rectangle', kind: 'rect', width: 94, height: 44 },
  { title: 'Circle', kind: 'circle', radius: 47 },

  // Row 2
  { title: 'Star', kind: 'path', d: starPath(5, 47, 0.55) },
  { title: 'Star (thin)', kind: 'path', d: starPath(5, 47, 0.38) },
  { title: 'Heart', kind: 'path', d: HEART_PATH },
  { title: 'Cloud', kind: 'path', d: CLOUD_PATH },
  { title: 'Triangle', kind: 'path', d: regularPolygonPath(3, 50) },
  { title: 'Triangle (tall)', kind: 'path', d: 'M50,2 L92,98 L8,98 Z' },
  {
    title: 'Square (rounded)',
    kind: 'rect',
    width: 94,
    height: 94,
    rx: 18,
    ry: 18,
  },

  // Row 2
  {
    title: 'Rectangle (rounded)',
    kind: 'rect',
    width: 94,
    height: 44,
    rx: 16,
    ry: 16,
  },
  { title: 'Diamond', kind: 'path', d: kitePath(0.5, 0.42) },
  {
    title: 'Diamond (gem)',
    kind: 'path',
    d: 'M28,14 L72,14 L94,42 L50,98 L6,42 Z',
  },
  { title: 'Spade', kind: 'path', d: SPADE_PATH },
  { title: 'Club', kind: 'path', d: CLUB_PATH },
  { title: 'Hexagon', kind: 'path', d: regularPolygonPath(6, 48, -90) },
  { title: 'Hexagon (flat)', kind: 'path', d: regularPolygonPath(6, 48, -60) },
  { title: 'Octagon', kind: 'path', d: regularPolygonPath(8, 48) },
  {
    title: 'Octagon (rotated)',
    kind: 'path',
    d: regularPolygonPath(8, 48, -67.5),
  },
  { title: 'Shield', kind: 'path', d: SHIELD_PATH },

  // Row 3
  { title: 'Quarter circle', kind: 'path', d: quarterCirclePath() },
  { title: 'Teardrop', kind: 'path', d: TEARDROP_PATH },
  { title: 'Lightning bolt', kind: 'path', d: LIGHTNING_PATH },
  { title: 'Sparkle', kind: 'path', d: starPath(4, 48, 0.28) },
  { title: 'Parallelogram', kind: 'path', d: parallelogramPath(0.28) },
  { title: 'Parallelogram (narrow)', kind: 'path', d: parallelogramPath(0.15) },
  { title: 'Pentagon', kind: 'path', d: regularPolygonPath(5, 49) },
  {
    title: 'Pentagon (house)',
    kind: 'path',
    d: 'M50,4 L94,38 L94,96 L6,96 L6,38 Z',
  },
  { title: 'Ellipse', kind: 'ellipse', rx: 48, ry: 30 },
  { title: 'Crescent moon', kind: 'path', d: CRESCENT_PATH },

  // Row 4
  { title: 'Rounded bar', kind: 'rect', width: 44, height: 94, rx: 18, ry: 18 },
  {
    title: 'Rounded bar (thin)',
    kind: 'rect',
    width: 30,
    height: 94,
    rx: 14,
    ry: 14,
  },
  { title: 'Seal', kind: 'path', d: scallopPath(SEAL_LOBES, 42, 10) },
  { title: 'Trapezoid', kind: 'path', d: trapezoidPath(0.55) },
  { title: 'Trapezoid (narrow)', kind: 'path', d: trapezoidPath(0.3) },
  { title: 'Kite', kind: 'path', d: kitePath(0.65, 0.35) },
  { title: 'Kite (small)', kind: 'path', d: kitePath(0.45, 0.42) },
  { title: 'X mark', kind: 'path', d: xMarkPath(0.3) },
  { title: 'X mark (thin)', kind: 'path', d: xMarkPath(0.18) },
  { title: 'Speech bubble (oval)', kind: 'path', d: SPEECH_BUBBLE_OVAL_PATH },

  // Row 5
  { title: 'Speech bubble', kind: 'path', d: SPEECH_BUBBLE_RECT_PATH },
  { title: 'Right triangle', kind: 'path', d: 'M6,6 L94,94 L6,94 Z' },
  {
    title: 'Right triangle (small)',
    kind: 'path',
    d: 'M20,20 L80,80 L20,80 Z',
  },
  { title: 'Plus', kind: 'path', d: plusPath(0.34) },
  { title: 'Plus (thin)', kind: 'path', d: plusPath(0.2) },
  { title: 'Flower', kind: 'path', d: scallopPath(8, 40, 12) },
  { title: 'Pac-Man', kind: 'path', d: pacmanPath(70) },
  { title: 'Ring', kind: 'path', d: ringPath(47, 32), fillRule: 'evenodd' },
  { title: 'Half circle', kind: 'path', d: halfCirclePath('top') },
  {
    title: 'Half circle (tall)',
    kind: 'path',
    d: 'M20,95 L20,50 A30,45 0 0 1 80,50 L80,95 Z',
  },

  // Row 6
  { title: 'Star 6', kind: 'path', d: starPath(6, 47, 0.55) },
  { title: 'Star 6 (thin)', kind: 'path', d: starPath(6, 47, 0.4) },
  { title: 'Flower (scallop)', kind: 'path', d: scallopPath(6, 38, 14) },
  {
    title: 'Flower (hollow)',
    kind: 'path',
    d: `${scallopPath(8, 42, 10)} ${scallopPath(8, 18, 4)}`,
    fillRule: 'evenodd',
  },
  { title: 'Egg', kind: 'path', d: EGG_PATH },
  { title: 'Leaf', kind: 'path', d: LEAF_PATH },
  { title: 'Crown', kind: 'path', d: CROWN_PATH },
  { title: 'Heptagon', kind: 'path', d: regularPolygonPath(7, 48) },
  { title: 'Nonagon', kind: 'path', d: regularPolygonPath(9, 48) },
  { title: 'Arch', kind: 'path', d: archPath(0.45) },

  // Row 7
  { title: 'Pin', kind: 'path', d: PIN_PATH },
  {
    title: 'Pin (hollow)',
    kind: 'path',
    d: PIN_HOLLOW_PATH,
    fillRule: 'evenodd',
  },
  { title: 'Arch (tall)', kind: 'path', d: archPath(0.65) },
  { title: 'Sparkle star 8', kind: 'path', d: starPath(8, 47, 0.55) },
  { title: 'Sparkle star 8 (thin)', kind: 'path', d: starPath(8, 47, 0.42) },
  {
    title: 'Sparkle star 8 (hollow)',
    kind: 'path',
    d: `${starPath(8, 47, 0.42)} ${starPath(8, 20, 0.42)}`,
    fillRule: 'evenodd',
  },
  { title: 'Small diamond', kind: 'path', d: kitePath(0.35, 0.5) },
  { title: 'Small diamond (gem)', kind: 'path', d: kitePath(0.5, 0.5) },
  { title: 'Arrow up', kind: 'path', d: arrowPath('up', 0.95, 0.4) },
  { title: 'Arrow up (thin)', kind: 'path', d: arrowPath('up', 0.7, 0.22) },

  // Row 8
  { title: 'Arrow down', kind: 'path', d: arrowPath('down', 0.95, 0.4) },
  { title: 'Arrow down (thin)', kind: 'path', d: arrowPath('down', 0.7, 0.22) },
  { title: 'Arrow left', kind: 'path', d: arrowPath('left', 0.95, 0.4) },
  { title: 'Arrow left (thin)', kind: 'path', d: arrowPath('left', 0.7, 0.22) },
  { title: 'Arrow right', kind: 'path', d: arrowPath('right', 0.95, 0.4) },
  {
    title: 'Arrow right (thin)',
    kind: 'path',
    d: arrowPath('right', 0.7, 0.22),
  },
  { title: 'Bookmark', kind: 'path', d: bookmarkPath() },
  {
    title: 'Bookmark (wide)',
    kind: 'path',
    d: 'M8,3 L92,3 L92,97 L50,74 L8,97 Z',
  },
  { title: 'Chevron arrow', kind: 'path', d: chevronArrowPath() },
  {
    title: 'Chevron arrow (long)',
    kind: 'path',
    d: 'M6,20 L50,20 L94,50 L50,80 L6,80 L28,50 Z',
  },

  // Row 9
  { title: 'Pill', kind: 'rect', width: 94, height: 40, rx: 20, ry: 20 },
  {
    title: 'Pill (rounded square)',
    kind: 'rect',
    width: 90,
    height: 70,
    rx: 26,
    ry: 26,
  },
  { title: 'Clover', kind: 'path', d: scallopPath(3, 34, 18) },
  { title: 'Flower (clover)', kind: 'path', d: scallopPath(4, 36, 16) },
  {
    title: 'Gear',
    kind: 'path',
    d: gearPath(8, 46, 36, 14),
    fillRule: 'evenodd',
  },
  {
    title: 'Octagon (stop)',
    kind: 'path',
    d: regularPolygonPath(8, 48, -22.5),
  },
  { title: 'Checkmark', kind: 'path', d: CHECKMARK_PATH },
  { title: 'Hourglass', kind: 'path', d: HOURGLASS_PATH },
  { title: 'Capsule', kind: 'rect', width: 30, height: 94, rx: 15, ry: 15 },
  {
    title: 'Capsule (wide)',
    kind: 'rect',
    width: 94,
    height: 26,
    rx: 13,
    ry: 13,
  },
];
